import { EmptyContainer, FaIcon, FaIcons } from '@innovationnorway/component-library';
import cn from 'classnames';
import { DateTime } from 'luxon';
import { FC, KeyboardEventHandler, useState } from 'react';
import { documentList } from '../../assets/localizations/localizationsDocumentList';
import { BANKING_INTEREST_RATE_CHANGE_DOCUMENT_REGEX } from '../../constants/documentListHelpers';
import { useDateUtils, useLocalizer, useResponsiveView } from '../../hooks';
import { ApiSchema } from '../../lib/query/apiClient';
import { DocumentType } from '../../models/digibank-mypage.models';
import { LoadingSpinner } from '../Shared/LoadingSpinner/LoadingSpinner';

export type DocumentDtoWithProjectName = ApiSchema<'DocumentDtoWithProjectName'>;
export type DocumentDto = ApiSchema<'DocumentDto'>;

interface DocumentListProps {
  documents: (DocumentDtoWithProjectName | DocumentDto)[];
  isLoaded: boolean;
  onDownload: (document: DocumentDtoWithProjectName | DocumentDto) => Promise<void>;
}

interface DocumentListItemProps {
  document: DocumentDtoWithProjectName | DocumentDto;
  onDownload: () => Promise<void>;
}

const DocumentListItem: FC<DocumentListItemProps> = (props) => {
  const [downloading, setDownloading] = useState<boolean>(false);

  const { toFormattedCoreViewDate } = useDateUtils();
  const { width } = useResponsiveView();
  const { typeSafeL } = useLocalizer('DocumentList');
  const documentListLocalizer = typeSafeL<typeof documentList>;

  const documentNeedsProjectName = (doc: string | undefined | null) => {
    const normalized = doc?.toLowerCase();
    return normalized === 'tilbud' || normalized === 'avslagsbrev';
  };

  const backEndToLocalizerMapping = {
    tilbud: documentListLocalizer('offerLetter'),
    avslagsbrev: documentListLocalizer('rejectionLetter'),
    'tilbud om vekstgaranti': documentListLocalizer('backingGuaranteeApproval'),
    'avslag av vekstgaranti': documentListLocalizer('backingGuaranteeRejection'),
  };

  const downloadFile = () => {
    setDownloading(true);
    props.onDownload().finally(() => setDownloading(false));
  };

  const downloadFileByEnter: KeyboardEventHandler<HTMLLIElement> = (key) => {
    if (key.code === 'Enter') {
      downloadFile();
    }
  };

  const getFormattedDate = () => {
    return toFormattedCoreViewDate(props.document.created, width <= 760 ? DateTime.DATE_SHORT : DateTime.DATE_MED);
  };

  const returnDefaultText = () => {
    let baseText = props.document.description ?? props.document.name;
    const normalizedBaseText = baseText?.toLowerCase();

    const projectName = Object.hasOwn(props.document, 'projectName') ? (props.document as DocumentDtoWithProjectName).projectName : undefined;

    if (documentNeedsProjectName(baseText) && projectName && normalizedBaseText && Object.prototype.hasOwnProperty.call(backEndToLocalizerMapping, normalizedBaseText)) {
      baseText = `${backEndToLocalizerMapping[normalizedBaseText as keyof typeof backEndToLocalizerMapping]} - ${projectName ?? ''}`;
    }
    return baseText;
  };

  const getLocalizedApplicationText = (name: string, description: string): string | null => {
    const n = name.trim().toLowerCase();
    const d = description.trim();

    if (d.toLowerCase().startsWith('vedtak')) {
      const decisionType = description.substring('vedtak'.length).trim();
      return decisionType ? `${documentListLocalizer('decision')} - ${decisionType}` : documentListLocalizer('decision');
    }
    if (n.startsWith('tilbud om vekstgaranti')) {
      const trailingText = name.substring('tilbud om vekstgaranti'.length).trimStart();
      return `${documentListLocalizer('backingGuaranteeApproval')} - ${trailingText}`;
    }
    if (n.startsWith('avslag av vekstgaranti')) {
      const trailingText = name.substring('avslag av vekstgaranti'.length).trimStart();
      return `${documentListLocalizer('backingGuaranteeRejection')} - ${trailingText}`;
    }
    return null;
  };

  const getDisplayText = () => {
    switch (props.document.documentType) {
      case DocumentType.ApplicationDocument: {
        const name = props.document.name ?? '';
        const description = props.document.description ?? '';
        const applicationText = getLocalizedApplicationText(name, description);
        if (applicationText) return applicationText;
        return returnDefaultText();
      }
      case DocumentType.BankingAnnualStatement:
        return props.document.description;
      case DocumentType.BankingDeferralPaymentDocument:
        return props.document.name;
      case DocumentType.BankingInterestRateChangeDocument: {
        const source = props.document.description ?? props.document.name ?? '';
        const match = source.match(BANKING_INTEREST_RATE_CHANGE_DOCUMENT_REGEX);
        if (match && match[1]) {
          return `${documentListLocalizer('bankingInterestRateChangeLetter')} - ${match[1]}`;
        }
        return documentListLocalizer('bankingInterestRateChangeLetter');
      }
      default: {
        return returnDefaultText();
      }
    }
  };

  return (
    <li
      className={cn('flex items-center text-base border-solid border-0 border-b border-in-black-25 p-5 cursor-pointer bg-white', {
        'cursor-default': downloading,
        'font-bold': !props.document.userHasRead,
      })}
      onClick={(e) => {
        e.preventDefault();
        downloadFile();
      }}
      onKeyDown={downloadFileByEnter}
      tabIndex={0}
    >
      <div className="flex items-center pr-2">
        <div
          className={cn('flex items-center justify-center w-2.5 h-2.5 rounded-full bg-in-blue-button-hover mr-4', {
            invisible: props.document.userHasRead,
          })}
        />
        <FaIcon name={FaIcons.FileDownloadLight} size="sm" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="overflow-hidden underline whitespace-nowrap text-ellipsis hover:no-underline">{getDisplayText()}</div>
      </div>
      <div className="pl-4 text-right whitespace-nowrap">{getFormattedDate()}</div>
    </li>
  );
};

export const DocumentList: FC<DocumentListProps> = (props: DocumentListProps) => {
  const { l } = useLocalizer('DocumentList');

  if (!props.isLoaded) return <LoadingSpinner />;

  // Replace decimal dots with commas in filenames to avoid multiple-dot confusion
  const sanitizeDocumentName = (name: string | null | undefined) => {
    return (name ?? '').replace(/(\d+)\.(\d+)/g, '$1,$2');
  };

  const customerDocuments = props.documents.length ? (
    props.documents.map((document) => {
      const docForDownload = { ...document, name: sanitizeDocumentName(document.name) };
      return <DocumentListItem key={document.id} document={document} onDownload={() => props.onDownload(docForDownload)} />;
    })
  ) : (
    <EmptyContainer>{l('noDocumentsLabel')}</EmptyContainer>
  );

  return (
    <ul className="mt-4 list-none p-0" lang="nb-NO">
      <li className="border-solid border-0 border-t border-in-black-25" />
      {customerDocuments}
    </ul>
  );
};
